import type {
  EventTypeView,
  MarketId,
  MeetingBasedMarketView,
  MeetingView,
  RaceView
} from '@ps/web-sports-api-types';
import { useSelector } from 'react-redux';

import { useWidget } from '@swf/core/hooks/useWidget';
import type { RootState } from '@swf/core/store';

import type { MeetingMarketTemplatesModel } from '../models/market-templates/meetingBased';
import { notEmpty } from '../utils/notEmpty';

interface NormalisedMeetingBasedData {
  race: RaceView;
  eventType: EventTypeView;
  meeting: MeetingView;
  market: MeetingBasedMarketView | undefined;
  markets: Record<MarketId, MeetingBasedMarketView>;
}

interface Props {
  marketId: MarketId;
}

export const useNormalisedMeetingBasedData = (props: Props): NormalisedMeetingBasedData => {
  const { widgetSelect } = useWidget<never, MeetingMarketTemplatesModel>();

  const market = useSelector((state: RootState) => widgetSelect.market(state, props.marketId));

  const race = useSelector((state: RootState) => widgetSelect.race(state, market?.raceId ?? ''));

  const meeting = useSelector((state: RootState) => widgetSelect.meeting(state, race.meetingId));

  const eventType = useSelector((state: RootState) =>
    widgetSelect.eventType(state, race.eventTypeId)
  );

  const partialMarkets = useSelector(widgetSelect.markets);
  const markets = Object.entries(partialMarkets).reduce<Record<MarketId, MeetingBasedMarketView>>(
    (acc, [key, val]) => {
      if (val?.runners.some(notEmpty)) {
        acc[key] = val;
      }
      return acc;
    },
    {}
  );

  return {
    meeting,
    race,
    eventType,
    market,
    markets
  };
};
